import { T } from 'frontend';
import { IDB_CACHE_NAME } from 'frontend/logic/file-store/dropbox-fs';
import { BROWSER_FILES_DB_NAME } from 'frontend/logic/file-store/indexeddb-fs';
import {
  clearAllLocalStorageForUserDataRemoval,
  persistedState,
} from 'frontend/logic/persisted-state';
import type { ImportQualitySettings } from 'shared';
import type { FileStoreCache } from 'frontend/logic/file-store';
import type { WorkerClient } from 'frontend/worker/client';

export function removeDropboxAccessToken() {
  persistedState.dropboxOauth.remove();
  indexedDB.deleteDatabase(IDB_CACHE_NAME);
  return { type: 'remove-dropbox-oauth' as const };
}

export function setDropboxAccessToken(
  accessToken: string,
  expiresIn: number,
  refreshToken: string,
) {
  // Convert the expires into milliseconds, and end it at 90% of the time.
  const expires = Date.now() + expiresIn * 1000 * 0.9;

  const oauth: T.DropboxOauth = {
    accessToken,
    expires,
    refreshToken,
  };
  persistedState.dropboxOauth.write(oauth);

  return {
    type: 'set-dropbox-oauth' as const,
    oauth,
  };
}

export function removeBrowserFiles() {
  indexedDB.deleteDatabase(BROWSER_FILES_DB_NAME);
  return { type: 'remove-browser-files' as const };
}

export function removeAllStorage() {
  clearAllLocalStorageForUserDataRemoval();
  indexedDB.deleteDatabase(BROWSER_FILES_DB_NAME);
  indexedDB.deleteDatabase(IDB_CACHE_NAME);
  return { type: 'remove-all-storage' as const };
}

export function addFileStoreServer(server: T.FileStoreServer) {
  return { type: 'add-server' as const, server };
}

export function removeFileStoreServer(server: T.FileStoreServer) {
  return { type: 'remove-server' as const, server };
}

export function updateFileStoreServer(
  oldServer: T.FileStoreServer,
  newServer: T.FileStoreServer,
) {
  return { type: 'update-server' as const, oldServer, newServer };
}

export function draggingSplitter(isDragging: boolean) {
  return { type: 'dragging-splitter' as const, isDragging };
}

export function clearApiCache() {
  return { type: 'clear-api-cache' as const };
}

export function changeActiveFile(path: string) {
  return { type: 'change-active-file' as const, path };
}

export function modifyActiveFile(
  modifiedText: string,
  path: string,
  forceRefresh: boolean,
) {
  return {
    type: 'modify-active-file' as const,
    modifiedText,
    path,
    forceRefresh,
  };
}

export function viewListFiles(
  fileStoreName: T.FileStoreName,
  fileStoreServer: T.FileStoreServer | null,
  path: string,
) {
  return {
    type: 'view-list-files' as const,
    fileStoreName,
    fileStoreServer,
    path,
  };
}

export function viewMusic(fileStoreServer: T.FileStoreServer, path: string) {
  return {
    type: 'view-music' as const,
    fileStoreServer,
    path,
  };
}

export function viewLanguageCoach(
  coachPath: string,
  path: string,
  invalidateOldData = false,
) {
  return {
    type: 'view-language-coach' as const,
    coachPath,
    path,
    invalidateOldData,
  };
}

export function setLanguageCoachSection(
  section: T.LanguageCoachSection,
  coachPath: string,
  path: string,
) {
  return {
    type: 'set-language-coach-section' as const,
    section,
    coachPath,
    path,
  };
}

export function viewFile(path: string) {
  return { type: 'view-file' as const, path };
}

export function viewPDF(path: string) {
  return { type: 'view-pdf' as const, path };
}

export function viewImage(path: string) {
  return { type: 'view-image' as const, path };
}

export function viewMarkdown(path: string) {
  return { type: 'view-markdown' as const, path };
}

export function viewLinkDropbox() {
  return { type: 'view-link-dropbox' as const };
}

export function viewSettings() {
  return { type: 'view-settings' as const };
}

export function viewConnect() {
  return { type: 'view-connect' as const };
}

export function viewConnectFolder() {
  return { type: 'view-connect-folder' as const };
}

export function dismissMessage(generation: number) {
  return {
    type: 'dismiss-message' as const,
    generation,
  };
}

export function hideEditor(flag: boolean) {
  return {
    type: 'hide-editor' as const,
    flag,
  };
}

export function setEditorOnly(isEditorOnly: boolean) {
  persistedState.appEditorOnly.write(isEditorOnly);
  return {
    type: 'set-editor-only' as const,
    isEditorOnly,
  };
}

export function invalidatePath(path: string) {
  return { type: 'invalidate-path' as const, path };
}

export function shouldHideHeader(hide: boolean) {
  return { type: 'should-hide-header' as const, hide };
}

export function startRenameFile(path: string) {
  return { type: 'start-rename-file' as const, path };
}

export function stopRenameFile() {
  return { type: 'stop-rename-file' as const };
}

export function setSearchString(search: string) {
  return { type: 'set-search-string' as const, search };
}

export function setCopyFile(paths: string[], isCut: boolean) {
  return { type: 'set-copy-file' as const, paths, isCut };
}

export function clearCopyFile() {
  return { type: 'clear-copy-file' as const };
}

export function setDraggedFiles(paths: string[]) {
  return { type: 'set-dragged-files' as const, paths };
}

export function clearDraggedFiles() {
  return { type: 'clear-dragged-files' as const };
}

export function changeFileStore(
  fileStoreName: T.FileStoreName,
  fileStoreServer?: T.FileStoreServer,
) {
  return {
    type: 'change-file-system' as const,
    fileStoreName,
    fileStoreServer,
  };
}

export function connectIDBFS(idbfs: FileStoreCache) {
  return { type: 'connect-idbfs' as const, idbfs };
}

export function setWorkerClient(workerClient: WorkerClient | null) {
  return { type: 'set-worker-client' as const, workerClient };
}

export function stemFrequencyAnalysis(stems: T.Stem[]) {
  return {
    type: 'stem-frequency-analysis' as const,
    stems,
  };
}

export function selectStem(stemIndex: number) {
  return {
    type: 'select-stem' as const,
    stemIndex,
  };
}

export function learnStem(stem: string) {
  return {
    type: 'learn-stem' as const,
    stem,
  };
}

export function ignoreStem(stem: string) {
  return {
    type: 'ignore-stem' as const,
    stem,
  };
}

export function updateLearnedWords(words: Set<string>) {
  return {
    type: 'update-learned-words' as const,
    words,
  };
}

export function updateIgnoredWords(words: Set<string>) {
  return {
    type: 'update-ignored-words' as const,
    words,
  };
}

export function loadLanguageData(languageData: T.LanguageDataV1) {
  return {
    type: 'load-language-data' as const,
    languageData,
  };
}

export function setAreStemsActive(isActive: boolean) {
  return {
    type: 'set-are-stems-active' as const,
    isActive,
  };
}

export function setOpenAIApiKey(apiKey: string) {
  persistedState.openAIAPIKey.write(apiKey);
  return {
    type: 'set-open-ai-api-key' as const,
    apiKey,
  };
}

export function setHasOnboarded(value: boolean) {
  return {
    type: 'set-has-onboarded' as const,
    value,
  };
}

export function setExperimentalFeatures(value: boolean) {
  return {
    type: 'set-experimental-features' as const,
    value,
  };
}

export function setFileStoreCacheEnabled(cacheKey: string, value: boolean) {
  return {
    type: 'set-file-store-cache-enabled' as const,
    cacheKey,
    value,
  };
}

export function setInputMode(value: T.InputMode) {
  return {
    type: 'set-input-mode' as const,
    value,
  };
}

export function setDetectedPointerType(value: T.ResolvedInputMode) {
  return {
    type: 'set-detected-pointer-type' as const,
    value,
  };
}

export function setEditorAutocomplete(
  editor: 'markdown' | 'chordpro',
  value: boolean,
) {
  return {
    type: 'set-editor-autocomplete' as const,
    editor,
    value,
  };
}

export function setMusicImportQuality(value: ImportQualitySettings) {
  return {
    type: 'set-music-import-quality' as const,
    value,
  };
}

export function changeFileFocus(folder: string, fileFocus: string) {
  return {
    type: 'change-file-focus' as const,
    folder,
    fileFocus,
  };
}

export function setFileSelection(folder: string, paths: string[]) {
  return {
    type: 'set-file-selection' as const,
    folder,
    paths,
  };
}

export function setMusicPanelSelection(
  panel: T.MusicPanelType,
  values?: string[],
) {
  return {
    type: 'set-music-panel-selection' as const,
    panel,
    values,
  };
}

export function setMusicSelectedTracks(paths: string[]) {
  return {
    type: 'set-music-selected-tracks' as const,
    paths,
  };
}

export function setMusicServerMaxIndexVersion(version: number) {
  return {
    type: 'set-music-server-max-index-version' as const,
    version,
  };
}

export function setFileManagerRevealLabel(label: string | null) {
  return {
    type: 'set-file-manager-reveal-label' as const,
    label,
  };
}

export function setFileStoreContainerName(containerName: string | null) {
  return {
    type: 'set-file-store-container-name' as const,
    containerName,
  };
}

export function musicPlaybackReady() {
  return { type: 'music-playback-ready' as const };
}

export function musicPlaybackPlay() {
  return { type: 'music-playback-play' as const };
}

export function musicPlaybackPause() {
  return { type: 'music-playback-pause' as const };
}

export function musicPlaybackError() {
  return { type: 'music-playback-error' as const };
}

export function musicPlaybackStop() {
  return { type: 'music-playback-stop' as const };
}

export function musicFolderArtworkSaveStart() {
  return { type: 'music-folder-artwork-save-start' as const };
}

export function musicFolderArtworkSaveSuccess() {
  return { type: 'music-folder-artwork-save-success' as const };
}

export function musicFolderArtworkSaveError() {
  return { type: 'music-folder-artwork-save-error' as const };
}

export function musicFolderArtworkEmbedStart() {
  return { type: 'music-folder-artwork-embed-start' as const };
}

export function musicFolderArtworkEmbedSuccess() {
  return { type: 'music-folder-artwork-embed-success' as const };
}

export function musicFolderArtworkEmbedError() {
  return { type: 'music-folder-artwork-embed-error' as const };
}

export function musicFolderArtworkRemoveStart() {
  return { type: 'music-folder-artwork-remove-start' as const };
}

export function musicFolderArtworkRemoveSuccess() {
  return { type: 'music-folder-artwork-remove-success' as const };
}

export function musicFolderArtworkRemoveError() {
  return { type: 'music-folder-artwork-remove-error' as const };
}

export function musicEmbeddedArtworkRemoveStart() {
  return { type: 'music-embedded-artwork-remove-start' as const };
}

export function musicEmbeddedArtworkRemoveSuccess() {
  return { type: 'music-embedded-artwork-remove-success' as const };
}

export function musicEmbeddedArtworkRemoveError() {
  return { type: 'music-embedded-artwork-remove-error' as const };
}

export function setMusicEditTrackPath(path: string | null) {
  return { type: 'set-music-edit-track-path' as const, path };
}

export function setMusicBatchEditTrackPaths(paths: string[] | null) {
  return { type: 'set-music-batch-edit-track-paths' as const, paths };
}

export function setMusicStagingPool(tracks: T.TrackMetadata[]) {
  return { type: 'set-music-staging-pool' as const, tracks };
}

export function setMusicStagingView(view: T.MusicStagingView | null) {
  return { type: 'set-music-staging-view' as const, view };
}

export function setMusicEditTab(tab: T.MusicEditTab) {
  return { type: 'set-music-edit-tab' as const, tab };
}
